/**
 * @fileoverview State, handlers, and helpers for the Change Password page.
 * @module pages/changePassword/useChangePasswordView
 */
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";

import {
  MIN_INPUT_LENGTH,
  OTP_MIN_LENGTH,
  OTP_INPUT_SIZE_DEFAULT,
  OTP_INPUT_SIZE_MEDIUM_MOBILE,
  OTP_INPUT_SIZE_SMALL_MOBILE,
  PASSWORD_SUCCESS_REDIRECT_DELAY_MS,
  MEDIUM_MOBILE_MAX_WIDTH_PX,
  MEDIUM_MOBILE_MIN_WIDTH_PX,
  SMALL_MOBILE_MAX_WIDTH_PX,
  TABLET_MAX_WIDTH_PX,
  TABLET_MIN_WIDTH_PX,
} from "./changePasswordConstants.js";
import {
  isPasswordValid,
  mapConfirmPasswordError,
} from "./changePasswordValidation.js";

const useChangePasswordView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const userEmail = location.state?.userEmail || "";

  const [confirmationCode, setConfirmationCode] = useState("");
  const [values, setValues] = useState({
    password: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
    errorMessage: "",
    showPasswordError: false,
    showCodeError: false,
    loading: false,
  });

  const handlePasswordChange = useCallback((event) => {
    setValues((prev) => ({ ...prev, password: event.target.value }));
  }, []);

  const handleConfirmPasswordChange = useCallback((event) => {
    setValues((prev) => ({ ...prev, confirmPassword: event.target.value }));
  }, []);

  const handleToggleShowPassword = useCallback(() => {
    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  }, []);

  const handleToggleShowConfirmPassword = useCallback(() => {
    setValues((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));
  }, []);

  const renderOtpInput = useCallback((props) => <input {...props} />, []);

  const handleNavigatePasswordRecovery = useCallback(
    () => navigate("/passwordRecovery"),
    [navigate],
  );

  const changePassword = useCallback(async () => {
    if (values.password !== values.confirmPassword) {
      setValues((prev) => ({
        ...prev,
        showPasswordError: true,
        showCodeError: false,
        errorMessage: "PASSWORDS_DO_NOT_MATCH",
      }));
      return;
    }
    if (!isPasswordValid(values.password)) {
      setValues((prev) => ({
        ...prev,
        showPasswordError: true,
        showCodeError: false,
        errorMessage: "INVALID_PASSWORD_FORMAT",
      }));
      return;
    }
    setValues((prev) => ({
      ...prev,
      loading: true,
      showPasswordError: false,
      showCodeError: false,
      errorMessage: "",
    }));

    const authParams = {
      email: userEmail,
      confirmationCode,
      password: values.password,
    };
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(authParams),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}auth/password/confirm`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(apiMessageOrGeneric(data?.message));
      }
      setTimeout(() => {
        setValues((prev) => ({ ...prev, loading: false }));
        navigate("/passwordSuccess");
      }, PASSWORD_SUCCESS_REDIRECT_DELAY_MS);
    } catch (error) {
      const { message, errorType } = mapConfirmPasswordError(error);
      setTimeout(() => {
        setValues((prev) => ({
          ...prev,
          loading: false,
          showPasswordError: errorType === "password",
          showCodeError: errorType === "confirmationCode",
          errorMessage: message,
        }));
      }, PASSWORD_SUCCESS_REDIRECT_DELAY_MS);
    }
  }, [values, confirmationCode, userEmail, navigate]);

  const buttonDisabled = useMemo(
    () =>
      values.loading ||
      values.password.length < MIN_INPUT_LENGTH ||
      values.confirmPassword.length < MIN_INPUT_LENGTH ||
      confirmationCode.length < OTP_MIN_LENGTH,
    [
      values.loading,
      values.password,
      values.confirmPassword,
      confirmationCode,
    ],
  );

  const isMediumMobile = useCallback(() => {
    return (
      window.innerWidth >= MEDIUM_MOBILE_MIN_WIDTH_PX &&
      window.innerWidth <= MEDIUM_MOBILE_MAX_WIDTH_PX
    );
  }, []);

  const isSmallMobile = useCallback(() => {
    return window.innerWidth <= SMALL_MOBILE_MAX_WIDTH_PX;
  }, []);

  const isTablet = useCallback(() => {
    return (
      window.innerWidth >= TABLET_MIN_WIDTH_PX &&
      window.innerWidth <= TABLET_MAX_WIDTH_PX
    );
  }, []);

  const getOtpInputSize = useCallback(() => {
    if (isTablet()) {
      return OTP_INPUT_SIZE_DEFAULT;
    }
    if (isMediumMobile()) {
      return OTP_INPUT_SIZE_MEDIUM_MOBILE;
    }
    if (isSmallMobile()) {
      return OTP_INPUT_SIZE_SMALL_MOBILE;
    }
    return OTP_INPUT_SIZE_DEFAULT;
  }, [isTablet, isMediumMobile, isSmallMobile]);

  return {
    t,
    userEmail,
    confirmationCode,
    setConfirmationCode,
    values,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleToggleShowPassword,
    handleToggleShowConfirmPassword,
    renderOtpInput,
    handleNavigatePasswordRecovery,
    changePassword,
    buttonDisabled,
    getOtpInputSize,
  };
};

export default useChangePasswordView;
